import { cn } from "../../foundations/cn.js";
import { BoxArt } from "../BoxArt.js";
import type { BattleRoundBox } from "./battleTypes.js";

export interface BattleRoundsStripProps {
  /** The battle's boxes in round order, one per round. */
  rounds: readonly BattleRoundBox[];
  /** The round in progress, highlighted; -1 or left out for none. */
  current?: number;
  /** How many show before the rest collapse to "+N". */
  max?: number;
  /** Each thumbnail's name, e.g. (index, name) => `Round ${index + 1}: ${name}`. */
  roundLabel?: (index: number, name: string) => string;
  size?: "sm" | "md";
  className?: string;
}

const SIZE = { sm: "h-9 w-7", md: "h-12 w-9" } as const;

/** A battle's boxes in round order, as small wrappers; the current round lifted and ringed. */
export function BattleRoundsStrip({
  rounds,
  current = -1,
  max = 8,
  roundLabel = (index, name) => `Round ${index + 1}: ${name}`,
  size = "md",
  className,
}: BattleRoundsStripProps) {
  const shown = rounds.slice(0, max);
  const rest = rounds.length - shown.length;
  return (
    <ol className={cn("flex min-w-0 items-center gap-1.5", className)}>
      {shown.map((round, index) => {
        const active = index === current;
        return (
          <li
            // Rounds repeat boxes, so the round's place is its identity.
            // biome-ignore lint/suspicious/noArrayIndexKey: the round index is the key
            key={index}
            aria-current={active ? "step" : undefined}
            className={cn(
              "shrink-0 rounded-sm p-0.5 transition-transform duration-base ease-standard motion-reduce:transition-none",
              SIZE[size],
              active ? "-translate-y-0.5 ring-2 ring-accent-default" : "opacity-90",
            )}
          >
            <BoxArt src={round.imageUrl} alt={roundLabel(index, round.name)} />
          </li>
        );
      })}
      {rest > 0 && (
        <li className="shrink-0 font-mono text-mono-meta text-text-tertiary">+{rest}</li>
      )}
    </ol>
  );
}
